import { AnalyticsService } from 'base/analytics';
import { useRightClickMenu } from 'base/ui/menu';
import {
  AnyMenuItem,
  menuDivider,
  menuItem,
  MenuModel,
} from 'base/ui/menu_model';
import React from 'react';
import { shortcut, useKeyboardShortcuts } from '../../keyboard_shortcuts';
import { PageEditingActions } from '../editing_actions/page';
import { ProjectEditingActions } from '../editing_actions/project';
import { Page, PageState } from '../model';
import {
  pageAskAiMenu,
  pathAskAiMenu,
  SelectionActions,
  selectionActionsMenu,
} from '../selection_actions';

export function useCommonPageInteractions({
  selectionActions,
  analytics,
  projectEditing,
  pageEditing,
  isSelected,
  showPathOption,
  extraMenuItems = [],
}: {
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  projectEditing: ProjectEditingActions | undefined;
  pageEditing: PageEditingActions | undefined;
  isSelected: boolean;
  showPathOption?: boolean;
  extraMenuItems?: AnyMenuItem[];
}): [
  menu: MenuModel,
  onContextMenu: ((e: React.MouseEvent) => void) | undefined,
  menuPortal: React.ReactNode,
] {
  const addPage =
    pageEditing &&
    (() => {
      pageEditing.addPageNatural();
      analytics.track({
        event: 'blocks/add-page',
        method: 'blank',
      });
    });

  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter', { cmd: true })]: addPage,
  });

  const trackConvertToGhostPage = () => {
    analytics.track({
      event: 'blocks/ghost-page-conversion',
      to: 'ghost',
    });
  };
  const pageEditingMenuItems = [];
  if (pageEditing) {
    pageEditingMenuItems.push(
      ...[
        menuDivider(),
        menuItem({
          label: 'Set as home page',
          onClick: pageEditing.setAsHomePage,
          disabled: !pageEditing.setAsHomePage,
          disabledReason: 'This page is already the home page',
        }),
      ],
    );

    if (showPathOption) {
      pageEditingMenuItems.push(
        menuItem({
          label: 'Change to page group',
          onClick: () => {
            pageEditing.convertToPath();
            trackConvertToGhostPage();
          },
        }),
      );
    }
  }

  const menu =
    pageEditing == null
      ? []
      : [
          selectionActionsMenu(
            selectionActions,
            pageAskAiMenu(selectionActions.editing),
          ),
          addPage && projectEditing
            ? [
                menuDivider(),
                menuItem({
                  label: 'Add page',
                  onClick: addPage,
                  shortcut: '⌘↵',
                }),
                menuItem({
                  label: 'Add section',
                  onClick: () =>
                    projectEditing.sidebarNavigation.toggleToplevel('add'),
                }),
              ]
            : [],
          pageEditingMenuItems,
          extraMenuItems.length ? [menuDivider(), ...extraMenuItems] : [],
        ].flat();

  const [onContextMenu, menuPortal] = useRightClickMenu(menu);
  return [menu, onContextMenu, menuPortal];
}

/**
 * Path pages have fewer editing options than pages, and are only visible in
 * sitemap mode.
 */
export function usePathEditMenu({
  selectionActions,
  pageEditing,
  analytics,
}: {
  page: Page | PageState;
  selectionActions: SelectionActions;
  pageEditing: PageEditingActions | undefined;
  analytics: AnalyticsService;
}): [
  menu: MenuModel,
  onContextMenu: ((e: React.MouseEvent) => void) | undefined,
  menuPortal: React.ReactNode,
] {
  const trackConvertToPage = () => {
    analytics.track({
      event: 'blocks/ghost-page-conversion',
      to: 'page',
    });
  };
  const menu = [
    selectionActionsMenu(
      selectionActions,
      pathAskAiMenu(selectionActions.editing),
    ),
    menuDivider(),
    menuItem({
      label: 'Change to page',
      onClick: () => {
        pageEditing?.convertToPage();
        trackConvertToPage();
      },
    }),
  ].flat();

  const [onContextMenu, menuPortal] = useRightClickMenu(menu);
  return [menu, onContextMenu, menuPortal];
}
